import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Gavel, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { Snippet } from '@/components/common/Snippet';
import { SaveSearchControl } from '@/components/common/WorkspaceControls';
import { PautasPanel } from '@/pages/cade/PautasPanel';
import { RecordingLink } from '@/pages/cade/RecordingLink';
import { ThesaurusNotice } from '@/pages/cade/ThesaurusNotice';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { formatSessionDate } from '@/lib/datetime';
import { cadeDecisionParams, fetchCadeDecisions } from '@/services/cade';
import type { CadeCitationLine, CadeDecisionHit, CadeDecisionList, CadeDecisionQuery } from '@/services/types';

const PAGE_SIZE = 25;
//: Citations drawn on a hit card before the remainder is counted.
const CITATIONS_SHOWN = 6;

/**
 * Decision search: the Tribunal's decisions as minuted, by wording, rapporteur
 * or case number.
 *
 * CADE has a case list but had no decision list, so a case with four items
 * resolved across four sessions could only be read one case at a time. This is
 * the other axis.
 *
 * Two things about it are easy to get wrong and both are stated on the face of
 * the screen rather than in a footnote, because each produces a result that
 * looks like a successful search:
 *
 * 1. A query of pure stopwords compiles to zero lexemes and can never match. It
 *    is refused BEFORE the scan, because "no results" on this screen is a claim
 *    about what the Tribunal has decided, and that claim would be false.
 * 2. Text matching reaches the decisions carrying the text SEARCHED: the
 *    "Decisão:" clause exists for most decisions, the whole ata item -- the
 *    clause plus its header naming parties, counsel and relator -- for all of
 *    them. The scope select picks one, and the reach line says which ran.
 *
 * Accents fold both ways -- `concentracao` and `concentração` find the same
 * decisions -- so the snippets are folded too. That is deliberate:
 * highlighting has to run over the same text the match ran over, or it marks
 * nothing and reports success.
 *
 * The URL carries the filters (`?q=`, `scope=`, `citation=`, `year=`), so a
 * doctrine line, a party record or a colleague can link straight to a
 * narrowed search, and the page writes them back on every change.
 */
export function CadeDecisionsPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState(searchParams.get('q') ?? '');
  const [query, setQuery] = useState<CadeDecisionQuery>(() => queryFromUrl(searchParams));

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['cade', 'decisions', query],
    queryFn: () => fetchCadeDecisions(query),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const offset = query.offset ?? 0;
  const update = (next: Partial<CadeDecisionQuery>) =>
    setQuery((current) => {
      const merged = { ...current, ...next, offset: 0 };
      setSearchParams(urlFromQuery(merged), { replace: true });
      return merged;
    });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:decisions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:decisions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:decisions.explainer" />

      <FieldGuide i18nKey="cade:decisions.guide" level={2} />

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <FilterForm
        draft={draft}
        onDraftChange={setDraft}
        query={query}
        onUpdate={update}
        data={data}
      />

      {/* The newest pautas, by their own printed date: what the Tribunal is about to
          take up, beside the search for what it has decided. */}
      <PautasPanel />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <SaveSearchControl
          agency="cade"
          queryString={cadeDecisionParams(query).toString()}
          defaultLabel={query.query || t('common:workspace.searches')}
        />
        <ExportButton path={`/api/v1/cade/export/decisions?${cadeDecisionParams(query)}`} />
        <ExportButton
          path={`/api/v1/cade/export/decisions.docx?${cadeDecisionParams(query)}`}
          label={t('common:export.word')}
        />
      </div>

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-3">
          {/* Above the count, not below it. Below, the reader has already read
              "0 decisions match" as an answer about the tribunal. */}
          {data.search.value?.no_searchable_terms ? (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{t('cade:decisions.noSearchableTerms')}</span>
            </p>
          ) : null}

          <ThesaurusNotice groups={data.search.value?.expanded ?? []} />

          <Answer answer={data.search} help={t('cade:decisions.help')}>
            {(reach) => (
              <div className="rounded-lg border bg-card p-3">
                <p className="text-sm text-foreground" aria-live="polite">
                  {t('cade:decisions.summary', { total: number.format(reach.matches) })}
                  {isFetching ? ` · ${t('common:state.loading')}` : ''}
                </p>
                {/* Worded per scope: the two texts have two reaches, and the
                    figure must name the one that ran. */}
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(reach.scope === 'item' ? 'cade:decisions.textReachItem' : 'cade:decisions.textReach', {
                    withText: number.format(
                      reach.scope === 'item' ? reach.with_item_text : reach.with_text,
                    ),
                    decisions: number.format(reach.decisions),
                    share: percent.format(reach.text_coverage),
                  })}
                </p>
              </div>
            )}
          </Answer>

          <DoctrinePanel
            data={data}
            selected={query.citation ?? null}
            onSelectCitation={(citation) => update({ citation })}
          />

          {/* A search that never ran is not a search that found nothing. The
              alert above says so; this slot must not also print "no decision
              matches", which would answer a question nobody managed to ask. */}
          {data.search.value?.no_searchable_terms ? null : data.items.length === 0 ? (
            <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('cade:decisions.noMatches')}
            </p>
          ) : (
            <>
              <ul className="space-y-3">
                {data.items.map((hit, index) => (
                  <DecisionCard
                    key={`${hit.case_key}-${hit.item_number ?? index}`}
                    hit={hit}
                    scope={query.scope ?? 'decision'}
                  />
                ))}
              </ul>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => setQuery({ ...query, offset: Math.max(offset - PAGE_SIZE, 0) })}
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {t('cade:consistency.previous')}
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE_SIZE >= data.total}
                  onClick={() => setQuery({ ...query, offset: offset + PAGE_SIZE })}
                  className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {t('cade:consistency.next')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The filters a URL can carry, read once on mount. Anything else is UI state. */
function queryFromUrl(params: URLSearchParams): CadeDecisionQuery {
  const year = Number(params.get('year'));
  return {
    query: params.get('q') || null,
    scope: params.get('scope') === 'item' ? 'item' : 'decision',
    track:
      params.get('track') === 'concentration' || params.get('track') === 'other'
        ? (params.get('track') as 'concentration' | 'other')
        : null,
    citation: params.get('citation') || null,
    sessionYear: Number.isInteger(year) && year > 0 ? year : null,
    onlyFinal: params.get('final') === 'true',
    session: params.get('session') || null,
    limit: PAGE_SIZE,
    offset: 0,
  };
}

/** The inverse: only the non-default filters, so a plain visit has a plain URL. */
function urlFromQuery(q: CadeDecisionQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.query) params.set('q', q.query);
  if (q.scope === 'item') params.set('scope', 'item');
  if (q.track) params.set('track', q.track);
  if (q.citation) params.set('citation', q.citation);
  if (q.sessionYear) params.set('year', String(q.sessionYear));
  if (q.onlyFinal) params.set('final', 'true');
  if (q.session) params.set('session', q.session);
  return params;
}

/**
 * Text search plus the filter selects, kept as one form because every control
 * resets the same pagination. All options come from the response -- a value
 * the corpus stops carrying stops being offered, and no option is offered
 * that no decision could satisfy.
 */
function FilterForm({
  draft,
  onDraftChange,
  query,
  onUpdate,
  data,
}: {
  draft: string;
  onDraftChange: (value: string) => void;
  query: CadeDecisionQuery;
  onUpdate: (next: Partial<CadeDecisionQuery>) => void;
  data: CadeDecisionList | undefined;
}) {
  const { t } = useTranslation(['cade']);

  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
      onSubmit={(event) => {
        event.preventDefault();
        onUpdate({ query: draft.trim() || null });
      }}
    >
      <div className="min-w-[16rem] flex-1 space-y-1">
        <label htmlFor="cade-decision-query" className="text-xs font-medium text-foreground">
          {t('cade:decisions.search.label')}
        </label>
        <input
          id="cade-decision-query"
          type="search"
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder={t('cade:decisions.search.placeholder')}
          className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
        />
        <p className="text-xs text-muted-foreground">{t('cade:decisions.search.hint')}</p>
      </div>

      {/* Which text the search runs over. The clause is the ruling; the whole
          item adds the header -- parties, counsel, relator as minuted -- so a
          firm's name or a lawyer's finds every item that names them. */}
      <div className="space-y-1">
        <label htmlFor="cade-decision-scope" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.scope')}
        </label>
        <select
          id="cade-decision-scope"
          value={query.scope ?? 'decision'}
          onChange={(event) =>
            onUpdate({ scope: event.target.value === 'item' ? 'item' : 'decision' })
          }
          className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="decision">{t('cade:decisions.filters.scopeDecision')}</option>
          <option value="item">{t('cade:decisions.filters.scopeItem')}</option>
        </select>
      </div>

      {/* The two tracks first, the type inside them second: merger review and
          conduct enforcement are different analyses, so the concentration
          types are separable rather than one more line in a flat list. */}
      <div className="space-y-1">
        <label htmlFor="cade-decision-track" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.track')}
        </label>
        <select
          id="cade-decision-track"
          value={query.track ?? ''}
          onChange={(event) => {
            const track = (event.target.value || null) as 'concentration' | 'other' | null;
            // A selected type outside the new track could only return empty;
            // carry it only while the track still offers it.
            const keep = query.caseType && typesInTrack(data, track).includes(query.caseType);
            onUpdate({ track, caseType: keep ? query.caseType : null });
          }}
          className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="">{t('cade:decisions.filters.trackAny')}</option>
          <option value="concentration">{t('cade:decisions.filters.trackConcentration')}</option>
          <option value="other">{t('cade:decisions.filters.trackOther')}</option>
        </select>
      </div>

      <div className="space-y-1">
        <label htmlFor="cade-decision-case-type" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.caseType')}
        </label>
        {/* Options are scoped to decision-carrying cases: the registry-only
            rows spell the same concepts differently, and offering them here
            would seed a filter no decision can satisfy. One type the registry
            spells two ways is offered once, under its majority spelling, and
            the groups separate merger review from the conduct track. */}
        <select
          id="cade-decision-case-type"
          value={query.caseType ?? ''}
          onChange={(event) => onUpdate({ caseType: event.target.value || null })}
          className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="">{t('cade:decisions.filters.caseTypeAny')}</option>
          <CaseTypeGroups data={data} track={query.track ?? null} />
        </select>
      </div>

      <div className="space-y-1">
        <label htmlFor="cade-decision-outcome" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.outcome')}
        </label>
        <select
          id="cade-decision-outcome"
          value={query.outcomeCode ?? ''}
          onChange={(event) => onUpdate({ outcomeCode: event.target.value || null })}
          className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="">{t('cade:decisions.filters.outcomeAny')}</option>
          {(data?.outcome_codes ?? []).map((code) => (
            <option key={code} value={code}>
              {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label htmlFor="cade-decision-relator" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.relator')}
        </label>
        <select
          id="cade-decision-relator"
          value={query.relator ?? ''}
          onChange={(event) => onUpdate({ relator: event.target.value || null })}
          className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="">{t('cade:decisions.filters.relatorAny')}</option>
          {(data?.relators ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label htmlFor="cade-decision-year" className="block text-xs font-medium text-foreground">
          {t('cade:decisions.filters.year')}
        </label>
        <select
          id="cade-decision-year"
          value={query.sessionYear ?? ''}
          onChange={(event) =>
            onUpdate({ sessionYear: event.target.value ? Number(event.target.value) : null })
          }
          className="max-w-[10rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
        >
          <option value="">{t('cade:decisions.filters.yearAny')}</option>
          {(data?.years ?? []).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 py-1.5 text-sm text-foreground">
        <input
          type="checkbox"
          checked={query.onlyWithText ?? false}
          onChange={(event) => onUpdate({ onlyWithText: event.target.checked })}
          className="h-4 w-4 rounded border"
        />
        {query.scope === 'item'
          ? t('cade:decisions.filters.onlyWithItemText')
          : t('cade:decisions.filters.onlyWithText')}
      </label>

      <label className="flex items-center gap-2 py-1.5 text-sm text-foreground">
        <input
          type="checkbox"
          checked={query.onlyFinal ?? false}
          onChange={(event) => onUpdate({ onlyFinal: event.target.checked })}
          className="h-4 w-4 rounded border"
        />
        {t('cade:decisions.filters.onlyFinal')}
      </label>

      <button
        type="submit"
        className="rounded-md border bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
      >
        {t('cade:decisions.search.submit')}
      </button>

      {/* The citation filter has no select: it is set by clicking a doctrine
          line below, or arrives in the URL. Shown as a chip so a reader can see
          the list is narrowed to one ground, and clear it. */}
      {query.citation ? (
        <div className="flex w-full flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t('cade:decisions.filters.citation')}</span>
          <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-foreground">
            {query.citation}
          </span>
          <button
            type="button"
            onClick={() => onUpdate({ citation: null })}
            className="rounded-md border px-2 py-0.5 text-foreground"
          >
            {t('cade:decisions.filters.clearCitation')}
          </button>
        </div>
      ) : null}
      {/* Same shape for one session, set from the sessions list: no select, a
          chip and a clear. The key is the minuted number where there is one. */}
      {query.session ? (
        <div className="flex w-full flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">{t('cade:decisions.filters.session')}</span>
          <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 tabular-nums text-foreground">
            {query.session}
          </span>
          <Link to="/cade/sessions" className="text-primary underline-offset-4 hover:underline">
            {t('cade:decisions.filters.allSessions')}
          </Link>
          <button
            type="button"
            onClick={() => onUpdate({ session: null })}
            className="rounded-md border px-2 py-0.5 text-foreground"
          >
            {t('cade:decisions.filters.clearSession')}
          </button>
        </div>
      ) : null}
    </form>
  );
}

/**
 * The tribunal's outcome record organised by the ground a decision cites --
 * the "linha do tribunal" a litigator reaches for first.
 *
 * Three qualifications live on the face of the panel rather than in a
 * footnote, because each turns a true number into a wrong impression:
 *
 * 1. Only the final decisions carrying extracted citations appear at all, so
 *    each line describes its citing subset, not the tribunal. The Answer
 *    substrate carries that coverage and renders PARTIAL while it holds.
 * 2. Outcome columns count code occurrences. A decision carrying two codes
 *    lands in two columns and a decision citing two grounds lands on two
 *    lines, so a line's shares can sum past 100% -- stated in the intro, not
 *    silently absorbed.
 * 3. Lines exist only where a citation backs the display floor of decisions.
 *    When none does, the Answer refuses and says what was measured instead of
 *    presenting two-decision rows as doctrine.
 */
function DoctrinePanel({
  data,
  selected,
  onSelectCitation,
}: {
  data: { citation_lines: CadeDecisionList['citation_lines'] };
  selected: string | null;
  onSelectCitation: (citation: string) => void;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 });

  return (
    <section className="rounded-lg border bg-card p-4">
      <Answer answer={data.citation_lines} help={t('cade:decisions.doctrine.help')}>
        {(value) => (
          <div className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {t('cade:decisions.doctrine.title')}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('cade:decisions.doctrine.intro')}
              </p>
            </div>
            <ul className="space-y-2">
              {value.lines.map((line) => (
                <CitationLineRow
                  key={line.citation}
                  line={line}
                  number={number}
                  percent={percent}
                  selected={line.citation === selected}
                  onSelect={() => onSelectCitation(line.citation)}
                />
              ))}
            </ul>
          </div>
        )}
      </Answer>
    </section>
  );
}

function CitationLineRow({
  line,
  number,
  percent,
  selected,
  onSelect,
}: {
  line: CadeCitationLine;
  number: Intl.NumberFormat;
  percent: Intl.NumberFormat;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation(['cade']);

  return (
    <li
      className={
        selected
          ? 'space-y-1.5 rounded border border-primary/60 bg-primary/5 p-2.5'
          : 'space-y-1.5 rounded border p-2.5'
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {/* The ground is the drill-through: a count on this panel reaches the
            decisions it counts (no orphan numbers). A button, not a Link -- the
            list is on this same page, and the URL follows through `update`. */}
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="text-left text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          {line.citation}
        </button>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t('cade:decisions.doctrine.decisions', { count: line.decisions })}
        </span>
      </div>
      {/* Counts, never a sum or a ladder per ground: fine rows concentrate on a
          few decisions and would present one cartel case as a pattern. */}
      <p className="text-xs tabular-nums text-muted-foreground">
        {t('cade:decisions.doctrine.fined', { fined: line.fined_decisions, rows: line.fine_rows })}
      </p>
      <ul className="flex flex-wrap gap-1">
        {line.outcomes.map((outcome) => (
          <li
            key={outcome.code}
            className="rounded border bg-muted/40 px-1.5 py-0.5 text-xs text-foreground"
          >
            {t(`cade:overview.outcomes.${outcome.code}`, { defaultValue: outcome.code })}
            <span className="ml-1 tabular-nums text-muted-foreground">
              {number.format(outcome.count)} · {percent.format(outcome.share)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

function DecisionCard({ hit, scope }: { hit: CadeDecisionHit; scope: 'decision' | 'item' }) {
  const { t, i18n } = useTranslation(['cade']);
  const sessionDate = formatSessionDate(hit.session_date, hit.session_date_raw, i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const hasSearchedText = scope === 'item' ? hit.has_item_text : hit.has_text;
  const deferred = hit.outcome_codes.includes('VIEW_REQUESTED_DEFERRED');

  return (
    <li className="space-y-2 rounded-lg border bg-card p-3 text-xs">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link
          to={`/cade/c/${hit.case_key}`}
          className="tabular-nums text-sm text-primary underline-offset-4 hover:underline"
        >
          {hit.case_number}
        </Link>
        <span className="flex flex-wrap items-center gap-2 text-muted-foreground">
          {hit.item_number ? <span>{t('cade:case.item', { item: hit.item_number })}</span> : null}
          {hit.is_final_observed ? (
            <span className="flex items-center gap-1 rounded border px-1.5 py-0.5 text-foreground">
              <Gavel className="h-3 w-3" aria-hidden />
              {t('cade:decisions.final')}
            </span>
          ) : null}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">
        {hit.relator ? (
          <span>
            {hit.relator_slug ? (
              <Link to={`/cade/r/${hit.relator_slug}`} className="text-primary underline-offset-4 hover:underline">
                {t('cade:decisions.relator', { name: hit.relator })}
              </Link>
            ) : (
              t('cade:decisions.relator', { name: hit.relator })
            )}
          </span>
        ) : null}
        {/* The session's date where the corpus has one (read from its pauta header,
            else the ata's prose); the ata's prose as written where it could not be
            parsed. The archive year beside it is a folder, not a date, checked
            against the prose on the strip above. */}
        {hit.session_number ? (
          <span>{t('cade:decisions.session', { number: hit.session_number })}</span>
        ) : null}
        {hit.session_year !== null ? (
          <span className="tabular-nums">{hit.session_year}</span>
        ) : null}
        {sessionDate ? <span>{sessionDate}</span> : null}
        <RecordingLink date={hit.session_date} />
      </div>

      {/* Translated, like every other CADE screen that renders these codes
          (`CasesPage`, `CaseDetailPage`, `ConsistencyPage`, `ConselheiroPage`).
          This one printed the raw enum until 2026-09-10, so a reader saw
          CONDEMNED_FINED on the search results and "Condenado com multa"
          everywhere else, for the same decision. `defaultValue: code` keeps a
          new code from the ETL visible rather than blank. */}
      {hit.outcome_codes.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {hit.outcome_codes.map((code) => (
            <li key={code} className="rounded border bg-muted/40 px-1.5 py-0.5 text-foreground">
              {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
            </li>
          ))}
        </ul>
      ) : null}

      {/* What the decision cost and how it split -- the two things a reader
          scanning precedents wants beside the outcome. The amount is the sum
          minuted on THIS decision; on a deferred item it was minuted, not
          imposed (101 of 590 fine rows sit on such items), so the card says so
          rather than letting a vista read as a condemnation. UFIR-only rows are
          counted and never converted. */}
      {hit.fine_total_brl !== null || hit.ufir_only_fines > 0 || hit.is_unanimous !== null ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          {hit.fine_total_brl !== null ? (
            <span className="tabular-nums text-foreground">
              {t('cade:decisions.hit.fineTotal', { amount: currency.format(hit.fine_total_brl) })}
            </span>
          ) : null}
          {hit.ufir_only_fines > 0 ? (
            <span>{t('cade:decisions.hit.ufirOnly', { count: hit.ufir_only_fines })}</span>
          ) : null}
          {deferred && (hit.fine_total_brl !== null || hit.ufir_only_fines > 0) ? (
            <span className="text-amber-700 dark:text-amber-400">
              {t('cade:decisions.hit.deferredAmount')}
            </span>
          ) : null}
          {hit.is_unanimous !== null ? (
            <span>
              {hit.is_unanimous
                ? t('cade:decisions.hit.unanimous')
                : t('cade:decisions.hit.byMajority')}
              {hit.votes > 0 ? ` · ${t('cade:decisions.hit.votes', { count: hit.votes })}` : ''}
            </span>
          ) : hit.votes > 0 ? (
            <span>{t('cade:decisions.hit.votes', { count: hit.votes })}</span>
          ) : null}
        </div>
      ) : null}

      {hit.snippet ? (
        <p className="border-l-2 pl-2 leading-relaxed text-muted-foreground">
          <Snippet text={hit.snippet} />
        </p>
      ) : hasSearchedText ? null : (
        <p className="text-muted-foreground">
          {scope === 'item' ? t('cade:decisions.noItemText') : t('cade:decisions.noText')}
        </p>
      )}

      {/* Six shown; the rest are COUNTED rather than dropped, as the case
          list does with parties. A row citing nine grounds had read as citing
          six, with nothing on screen to say so. The export carries them all. */}
      {hit.legal_citations.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {hit.legal_citations.slice(0, CITATIONS_SHOWN).map((citation) => (
            <li key={citation} className="rounded border border-dashed px-1.5 py-0.5">
              {citation}
            </li>
          ))}
          {hit.legal_citations.length > CITATIONS_SHOWN ? (
            <li className="px-1.5 py-0.5 text-muted-foreground">
              {t('cade:decisions.moreCitations', {
                count: hit.legal_citations.length - CITATIONS_SHOWN,
              })}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The type labels a track offers: its own group, or every type while none is
 * set. The backend names the concentration members (`concentration_case_types`)
 * so the fold that classifies them is spelled once, server-side.
 */
function typesInTrack(
  data: CadeDecisionList | undefined,
  track: 'concentration' | 'other' | null,
): string[] {
  const types = data?.case_types ?? [];
  const concentration = data?.concentration_case_types ?? [];
  if (track === 'concentration') return concentration;
  if (track === 'other') return types.filter((type) => !concentration.includes(type));
  return types;
}

/**
 * The case-type options in two groups: the concentration track first, then
 * every other type. A type the payload does not flag renders in the second
 * group, and a chosen track offers only its own group -- a type outside it
 * could only filter to empty.
 */
function CaseTypeGroups({
  data,
  track,
}: {
  data: CadeDecisionList | undefined;
  track: 'concentration' | 'other' | null;
}) {
  const { t } = useTranslation(['cade']);
  const groups: [string, string[]][] = [
    ['cade:decisions.filters.typeGroupConcentration', typesInTrack(data, 'concentration')],
    ['cade:decisions.filters.typeGroupOther', typesInTrack(data, 'other')],
  ];
  const visible = track === 'concentration' ? groups.slice(0, 1) : track === 'other' ? groups.slice(1) : groups;

  return (
    <>
      {visible
        .filter(([, group]) => group.length > 0)
        .map(([label, group]) => (
          <optgroup key={label} label={t(label)}>
            {group.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </optgroup>
        ))}
    </>
  );
}
